<template>
  <div class="container-fluid p-4">
    <!-- Welcome Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="fade-in">
          <h2 class="mb-2 fw-bold gradient-text">{{ getWelcomeMessage() }}</h2>
          <p class="text-muted">{{ getSubtitleMessage() }}</p>
        </div>
      </div>
    </div>

    <!-- Class Selection Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex justify-content-between align-items-center">
          <h3 class="mb-0 fw-bold" style="color: var(--text-primary);">Teacher Dashboard</h3>
          <div class="d-flex gap-2 align-items-center">
            <button class="btn btn-success" style="border-radius: 20px;" @click="openChallengeModal">
              <i class="bi bi-trophy me-1"></i> Create Challenge
            </button>
            <button class="btn btn-outline-primary" style="border-radius: 20px;" @click="openBadgeModal">
              <i class="bi bi-award me-1"></i> Create Custom Badge
            </button>
            <select
            class="form-select"
            style="max-width: 260px; border: 2px solid var(--border-color); border-radius: 8px;"
            v-model="selectedClassCode"
            @change="onClassChange"
          >
            <option :value="null">Select Class...</option>
            <option
              v-for="cls in classes"
              :key="cls.class_code"
              :value="cls.class_code"
            >
              {{ formatClassLabel(cls) }}
            </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Class Inline Card -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="modern-card p-4" style="background: linear-gradient(135deg, #ecfeff 0%, #cffafe 100%);">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0 fw-bold" style="color: var(--text-primary);">Create a New Class</h5>
          </div>
          <div class="row g-3 align-items-end">
            <div class="col-md-3">
              <label class="form-label">Class Code</label>
              <input class="form-control" v-model="newClass.class_code" placeholder="e.g., ENG5A" />
            </div>
            <div class="col-md-4">
              <label class="form-label">Subject</label>
              <input class="form-control" v-model="newClass.subject_name" placeholder="e.g., English" />
            </div>
            <div class="col-md-3">
              <label class="form-label">Grade Level</label>
              <input class="form-control" v-model="newClass.grade_level" placeholder="e.g., 5" />
            </div>
            <div class="col-md-2 d-grid">
              <button class="btn btn-primary" :disabled="createClassLoading" @click="submitCreateClass">
                <span v-if="createClassLoading" class="spinner-border spinner-border-sm me-2"></span>
                Create Class
              </button>
            </div>
          </div>
          <div v-if="createClassMessage" class="alert alert-success mt-3">{{ createClassMessage }}</div>
          <div v-if="createClassError" class="alert alert-danger mt-3">{{ createClassError }}</div>
        </div>
      </div>
    </div>

    <!-- Create Challenge Modal -->
    <div v-if="showChallengeModal" class="modal fade show" style="display:block; background: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Create Challenge</h5>
            <button type="button" class="btn-close" @click="closeChallengeModal"></button>
          </div>
          <div class="modal-body">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">Title</label>
                <input class="form-control" v-model="newChallenge.title" />
              </div>
              <div class="col-md-6">
                <label class="form-label">Category</label>
                <input class="form-control" v-model="newChallenge.category" placeholder="e.g., Reading" />
              </div>
              <div class="col-md-12">
                <label class="form-label">Description</label>
                <textarea class="form-control" rows="2" v-model="newChallenge.description"></textarea>
              </div>
              <div class="col-md-4">
                <label class="form-label">Difficulty</label>
                <select class="form-select" v-model="newChallenge.difficulty">
                  <option>Beginner</option>
                  <option>Intermediate</option>
                  <option>Advanced</option>
                </select>
              </div>
              <div class="col-md-4">
                <label class="form-label">Points</label>
                <input type="number" class="form-control" v-model.number="newChallenge.points" />
              </div>
              <div class="col-md-4">
                <label class="form-label">Target Count</label>
                <input type="number" class="form-control" v-model.number="newChallenge.target_count" />
              </div>
              <div class="col-md-6">
                <label class="form-label">Start Date</label>
                <input type="date" class="form-control" v-model="newChallenge.start_date" />
              </div>
              <div class="col-md-6">
                <label class="form-label">End Date</label>
                <input type="date" class="form-control" v-model="newChallenge.end_date" />
              </div>
              <div class="col-md-12">
                <label class="form-label">Goals (optional)</label>
                <div v-for="(g, idx) in newChallenge.goals" :key="idx" class="d-flex gap-2 mb-2">
                  <input class="form-control" placeholder="Goal title" v-model="g.title" />
                  <input class="form-control" type="number" style="max-width:120px" placeholder="Target" v-model.number="g.target" />
                  <input class="form-control" type="number" style="max-width:120px" placeholder="Points" v-model.number="g.points" />
                  <button class="btn btn-outline-danger" @click="removeGoal(idx)">Remove</button>
                </div>
                <button class="btn btn-outline-primary btn-sm" @click="addGoal">+ Add Goal</button>
              </div>
            </div>
            <div v-if="challengeCreateError" class="alert alert-danger mt-3">{{ challengeCreateError }}</div>
            <div v-if="challengeCreateMessage" class="alert alert-success mt-3">{{ challengeCreateMessage }}</div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeChallengeModal">Close</button>
            <button class="btn btn-success" :disabled="creatingChallenge" @click="submitCreateChallenge">
              <span v-if="creatingChallenge" class="spinner-border spinner-border-sm me-2"></span>
              Create & Publish
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Custom Badge Modal -->
    <div v-if="showBadgeModal" class="modal fade show" style="display:block; background: rgba(0,0,0,0.5);">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Create Custom Badge</h5>
            <button type="button" class="btn-close" @click="closeBadgeModal"></button>
          </div>
          <div class="modal-body">
            <div class="row g-3">
              <div class="col-12">
                <label class="form-label">Title</label>
                <input class="form-control" v-model="newBadge.title" placeholder="e.g., Kind Helper" />
              </div>
              <div class="col-12">
                <label class="form-label">Description</label>
                <textarea class="form-control" rows="2" v-model="newBadge.description" placeholder="What does this badge reward?"></textarea>
              </div>
              <div class="col-md-6">
                <label class="form-label">Icon</label>
                <input class="form-control" v-model="newBadge.icon" placeholder="e.g., 🏅" />
              </div>
              <div class="col-md-6">
                <label class="form-label">Difficulty</label>
                <select class="form-select" v-model="newBadge.difficulty">
                  <option>Beginner</option>
                  <option>Intermediate</option>
                  <option>Advanced</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Required Count</label>
                <input type="number" class="form-control" v-model.number="newBadge.required" min="1" />
              </div>
              <div class="col-md-6">
                <label class="form-label">Category</label>
                <input class="form-control" v-model="newBadge.category" placeholder="e.g., Reading" />
              </div>
            </div>
            <div v-if="badgeCreateError" class="alert alert-danger mt-3">{{ badgeCreateError }}</div>
            <div v-if="badgeCreateMessage" class="alert alert-success mt-3">{{ badgeCreateMessage }}</div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeBadgeModal">Close</button>
            <button class="btn btn-primary" :disabled="creatingBadge" @click="submitCreateBadge">
              <span v-if="creatingBadge" class="spinner-border spinner-border-sm me-2"></span>
              Create Badge
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Dashboard Navigation Tabs -->
    <div class="row mb-4">
      <div class="col-12">
        <ul class="nav nav-tabs modern-tabs">
          <li class="nav-item">
            <button 
              class="nav-link"
              :class="{ active: activeTab === 'overview' }"
              @click="activeTab = 'overview'"
            >
              <i class="bi bi-house me-2"></i>Overview
            </button>
          </li>
          <li class="nav-item">
            <button 
              class="nav-link"
              :class="{ active: activeTab === 'assignments' }"
              @click="activeTab = 'assignments'"
            >
              <i class="bi bi-clipboard-check me-2"></i>Assignments
            </button>
          </li>
          <li class="nav-item">
            <button 
              class="nav-link"
              :class="{ active: activeTab === 'analytics' }"
              @click="activeTab = 'analytics'"
            >
              <i class="bi bi-graph-up me-2"></i>Analytics
            </button>
          </li>
        </ul>
      </div>
    </div>

    <!-- Overview Tab -->
    <div v-if="activeTab === 'overview'">
      <!-- Class List and Assign Module -->
      <div class="row g-4 mb-4">
        <div class="col-lg-8">
          <div class="modern-card p-4" style="background: linear-gradient(135deg, #ecfeff 0%, #cffafe 100%);">
            <h5 class="mb-4 fw-bold" style="color: var(--text-primary);">Class List</h5>
          <div class="modern-table">
            <table class="table">
              <thead>
                <tr>
                  <th style="color: var(--text-primary);">Student Name</th>
                  <th style="color: var(--text-primary);">Age</th>
                  <th style="color: var(--text-primary);">Level</th>
                  <th style="color: var(--text-primary);">Status</th>
                  <th style="color: var(--text-primary);">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(student, index) in students" :key="index">
                  <td style="color: var(--text-primary);">{{ student.name }}</td>
                  <td style="color: var(--text-secondary);">{{ student.age }}</td>
                  <td style="color: var(--text-secondary);">{{ student.level }}</td>
                  <td>
                    <span 
                      class="badge px-3 py-1"
                      :style="{ background: student.statusColor, color: 'white', borderRadius: '20px' }"
                    >
                      {{ student.status }}
                    </span>
                  </td>
                  <td>
                    <button 
                      class="btn btn-sm" 
                      style="background: var(--role-primary); color: white; border-radius: 20px;"
                      @click="viewStudentProfile(student.name)"
                    >
                      View Profile
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Create Assignment -->
      <div class="col-lg-4">
        <div class="modern-card p-4 h-100" style="background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);">
          <h5 class="mb-4 fw-bold" style="color: var(--text-primary);">Create Assignment</h5>
          <div class="text-center mb-4">
            <i class="bi bi-clipboard-check" style="font-size: 3rem; color: var(--role-primary); opacity: 0.8;"></i>
          </div>
          <p class="text-center mb-4" style="color: var(--text-secondary);">
            Create and manage assignments for your students with our comprehensive assignment manager.
          </p>
          <div class="d-grid gap-2">
            <button 
              class="btn" 
              style="background: #f97316; color: white; border-radius: 20px; font-weight: 600;"
              @click="activeTab = 'assignments'"
            >
              <i class="bi bi-plus-lg me-2"></i>Create New Assignment
            </button>
            <button 
              class="btn btn-outline-secondary" 
              style="border-radius: 20px;"
              @click="activeTab = 'assignments'"
            >
              <i class="bi bi-list-ul me-2"></i>Manage Assignments
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Announcements and Class Progress -->
    <div class="row g-4 mb-4">
      <div class="col-lg-8">
        <div class="modern-card p-4 h-100" style="background: linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%);">
          <h5 class="mb-4 fw-bold" style="color: var(--text-primary);">Announcements</h5>
          <div class="mb-4">
            <textarea 
              class="form-control" 
              rows="3" 
              placeholder="Write an announcement for your class..."
              v-model.trim="announcementContent"
              style="border: 2px solid var(--border-color); border-radius: 8px; background: var(--bg-primary); color: var(--text-primary);"
            ></textarea>
          </div>
          <div class="d-flex justify-content-between align-items-center">
            <div class="d-flex gap-2">
              <button class="btn btn-sm" style="background: #f59e0b; color: white; border-radius: 20px;" :disabled="sendingAnnouncement" @click="submitAnnouncementAll">
                📢 Send to All
              </button>
              <button class="btn btn-sm" style="background: #8b5cf6; color: white; border-radius: 20px;" :disabled="sendingAnnouncement" @click="submitAnnouncementParents">
                📧 Send to Parents
              </button>
            </div>
            <small style="color: var(--text-secondary);">Last announcement: 2 days ago</small>
          </div>
          <div v-if="announcementError" class="alert alert-danger mt-3">{{ announcementError }}</div>
          <div v-if="announcementMessage" class="alert alert-success mt-3">{{ announcementMessage }}</div>
        </div>
      </div>

      <div class="col-lg-4">
        <div class="modern-card p-4 h-100" style="background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);">
          <h5 class="mb-4 fw-bold" style="color: var(--text-primary);">Class Progress</h5>
          <div class="text-center mb-4">
            <div class="progress-ring" style="width: 100px; height: 100px;">
              <div class="progress-donut" :style="donutStyle"></div>
              <div class="progress-hole"></div>
              <div class="progress-ring-text" style="color: var(--role-primary);">{{ classProgress.completionRate }}%</div>
            </div>
            <p class="small mt-2 mb-2" style="color: var(--text-secondary);">Completed</p>
            <p class="small" style="color: var(--text-secondary);">Total Students: {{ classProgress.totalStudents }}</p>
          </div>
          <div class="row text-center">
            <div class="col-6">
              <div class="p-2">
                <h6 class="fw-bold" style="color: var(--text-primary);">{{ classProgress.averageScore }}%</h6>
                <small style="color: var(--text-secondary);">Avg. Score</small>
              </div>
            </div>
            <div class="col-6">
              <div class="p-2">
                <h6 class="fw-bold" style="color: var(--text-primary);">{{ classProgress.avgTimeSpent }}h</h6>
                <small style="color: var(--text-secondary);">Avg. Time Spent</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Student Performance Table -->
    <div class="row mt-4">
      <div class="col-12">
        <div class="modern-card p-4 performance-table-card">
          <h5 class="mb-4 fw-bold" style="color: var(--text-primary);">Student Performance</h5>
          <div class="modern-table">
            <table class="table">
              <thead>
                <tr>
                  <th style="color: var(--text-primary);">Student Name</th>
                  <th style="color: var(--text-primary);">Avg. Score</th>
                  <th style="color: var(--text-primary);">Badges Earned</th>
                  <th style="color: var(--text-primary);">Last Active</th>
                  <th style="color: var(--text-primary);">Progress</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(student, index) in performanceData" :key="index">
                  <td style="color: var(--text-primary);">{{ student.name }}</td>
                  <td class="fw-semibold" style="color: var(--role-primary);">{{ student.score }}</td>
                  <td>
                    <div class="d-flex align-items-center">
                      <span class="me-2">⭐</span>
                      <span style="color: var(--text-primary);">{{ student.badges }}</span>
                    </div>
                  </td>
                  <td style="color: var(--text-secondary);">{{ student.lastActive }}</td>
                  <td>
                    <div class="d-flex align-items-center">
                      <div class="progress me-2" style="width: 60px; height: 8px;">
                        <div 
                          class="progress-bar" 
                          :style="{ width: student.progress + '%', background: 'var(--role-primary)' }"
                        ></div>
                      </div>
                      <small style="color: var(--text-secondary);">{{ student.progress }}%</small>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- Assignments Tab -->
    <div v-if="activeTab === 'assignments'">
      <AssignmentManager />
    </div>

    <!-- Analytics Tab -->
    <div v-if="activeTab === 'analytics'">
      <StudentAnalytics />
    </div>
  </div>
</template>

<script>
import AssignmentManager from './AssignmentManager.vue'
import StudentAnalytics from './StudentAnalytics.vue'

export default {
  name: 'TeacherDashboard',
  components: {
    AssignmentManager,
    StudentAnalytics
  },
  props: {
    user: {
      type: Object,
      required: false
    }
  },
  data() {
    return {
      currentUser: null,
      activeTab: 'overview',
      selectedClassCode: null,
      classes: [],
      students: [],
      performanceData: [],
      classProgress: {
        completionRate: 0,
        averageScore: 0,
        avgTimeSpent: 0,
        totalStudents: 0,
        ringAngle: 0
      },
      // Create class form state
      newClass: { class_code: '', subject_name: '', grade_level: '' },
      createClassLoading: false,
      createClassMessage: '',
      createClassError: '',
      // Challenge modal state
      showChallengeModal: false,
      creatingChallenge: false,
      challengeCreateError: '',
      challengeCreateMessage: '',
      newChallenge: {
        title: '', description: '', category: '', difficulty: 'Beginner',
        duration: '7 days', start_date: '', end_date: '', target_count: 1,
        points: 100, icon: 'bi bi-trophy', goals: []
      },
      // Badge modal state
      showBadgeModal: false,
      creatingBadge: false,
      badgeCreateError: '',
      badgeCreateMessage: '',
      newBadge: { title: '', description: '', icon: '🏅', difficulty: 'Beginner', required: 1, category: '' }
      ,
      // Announcements state
      announcementContent: '',
      sendingAnnouncement: false,
      announcementError: '',
      announcementMessage: ''
    }
  },
  async mounted() {
    // Get current user from localStorage or props
    const storedUser = localStorage.getItem('currentUser')
    if (storedUser) {
      this.currentUser = JSON.parse(storedUser)
    } else if (this.user) {
      this.currentUser = this.user
    }

    // Load classes and default class students
    await this.loadClasses()
    if (this.selectedClassCode) {
      await this.loadClassStudents(this.selectedClassCode)
    }
  },
  methods: {
    formatClassLabel(cls) {
      const grade = cls && cls.grade_level ? `Grade ${cls.grade_level}` : (cls?.class_code || 'Class')
      const subject = cls && cls.subject_name ? cls.subject_name : 'Class'
      return `${grade} - ${subject}`
    },
    async loadClasses() {
      try {
        const { getTeacherClasses } = await import('../services/teacherService.js')
        if (!this.currentUser?.id) return
        const res = await getTeacherClasses(this.currentUser.id)
        if (res?.success) {
          this.classes = res.data || []
          this.selectedClassCode = this.classes[0]?.class_code || null
        }
      } catch (e) {
        console.error('Failed to load classes:', e)
      }
    },
    async loadClassStudents(classCode) {
      try {
        const { getClassStudents, getTeacherAnalytics } = await import('../services/teacherService.js')
        const { getUserTasks } = await import('../services/taskService.js')
        const { getUserBadges } = await import('../services/badgeService.js')
        if (!this.currentUser?.id || !classCode) return
        const studentsRes = await getClassStudents(this.currentUser.id, classCode)
        const roster = (studentsRes?.success ? studentsRes.data : [])
        this.students = roster.map(s => ({
          name: s.full_name,
          age: s.grade ? parseInt(String(s.grade).replace(/\D/g, '')) || null : null,
          level: s.level,
          status: 'Active',
          statusColor: '#06b6d4'
        }))
        // Populate performanceData with basic metrics
        const rows = []
        for (const s of roster) {
          try {
            const [tasks, badges] = await Promise.all([
              getUserTasks(s.user_id),
              getUserBadges(s.user_id)
            ])
            const total = Array.isArray(tasks) ? tasks.length : 0
            const completed = (tasks || []).filter(t => (t.status || '').toLowerCase() === 'completed').length
            const progress = total ? Math.round((completed / total) * 100) : 0
            const lastDates = (tasks || [])
              .map(t => t.due_date || t.createdAt || t.created_date)
              .filter(Boolean)
              .map(d => new Date(d).getTime())
            const lastActiveTs = lastDates.length ? Math.max(...lastDates) : null
            const lastActive = lastActiveTs ? new Date(lastActiveTs).toLocaleDateString() : '—'
            const badgesCount = ((badges?.completed || []).length) || 0

            rows.push({
              name: s.full_name,
              score: 0, // Placeholder until quiz/assignment scoring is wired
              badges: badgesCount,
              lastActive,
              progress
            })
          } catch (e) {
            rows.push({ name: s.full_name, score: 0, badges: 0, lastActive: '—', progress: 0 })
          }
        }
        this.performanceData = rows
        // Pull analytics for the class to populate the right panel
        const analytics = await getTeacherAnalytics(this.currentUser.id, { timeframe: 'month', class_code: classCode })
        if (analytics?.success) {
          const t = analytics.data?.totals || {}
          this.classProgress.completionRate = Math.round(t.completion_rate || 0)
          this.classProgress.averageScore = Math.round(t.average_score || 0)
          this.classProgress.avgTimeSpent = t.average_time_spent || 0
          this.classProgress.totalStudents = t.total_students || 0
          this.classProgress.ringAngle = Math.min(360, Math.max(0, (this.classProgress.completionRate / 100) * 360))
        } else {
          this.classProgress = { completionRate: 0, averageScore: 0, avgTimeSpent: 0, totalStudents: 0, ringAngle: 0 }
        }
      } catch (e) {
        console.error('Failed to load class students:', e)
      }
    },
    async onClassChange() {
      if (this.selectedClassCode) {
        await this.loadClassStudents(this.selectedClassCode)
      } else {
        this.students = []
        this.performanceData = []
      }
    },
    async submitCreateClass() {
      try {
        this.createClassError = ''
        this.createClassMessage = ''
        if (!this.newClass.class_code || !this.newClass.subject_name) {
          this.createClassError = 'Class code and subject are required'
          return
        }
        this.createClassLoading = true
        const { createClass } = await import('../services/teacherService.js')
        const res = await createClass({
          class_code: this.newClass.class_code.trim(),
          subject_name: this.newClass.subject_name.trim(),
          grade_level: (this.newClass.grade_level || '').toString().trim(),
          teacher_id: this.currentUser?.id
        })
        if (res?.success) {
          this.createClassMessage = 'Class created successfully'
          this.newClass = { class_code: '', subject_name: '', grade_level: '' }
          await this.loadClasses()
        } else {
          this.createClassError = res?.error || 'Failed to create class'
        }
      } catch (e) {
        console.error('Create class failed:', e)
        this.createClassError = e?.message || 'Failed to create class'
      } finally {
        this.createClassLoading = false
      }
    },
    openChallengeModal() {
      this.challengeCreateError = ''
      this.challengeCreateMessage = ''
      this.showChallengeModal = true
    },
    closeChallengeModal() {
      this.showChallengeModal = false
    },
    addGoal() {
      this.newChallenge.goals.push({ title: '', target: 1, points: 10 })
    },
    removeGoal(idx) {
      this.newChallenge.goals.splice(idx, 1)
    },
    openBadgeModal() {
      this.badgeCreateError = ''
      this.badgeCreateMessage = ''
      this.showBadgeModal = true
    },
    closeBadgeModal() {
      this.showBadgeModal = false
    },
    async submitAnnouncementAll() {
      try {
        this.announcementError = ''
        this.announcementMessage = ''
        if (!this.currentUser?.id) {
          this.announcementError = 'Missing teacher id'
          return
        }
        if (!this.selectedClassCode) {
          this.announcementError = 'Please select a class'
          return
        }
        if (!this.announcementContent) {
          this.announcementError = 'Please enter announcement content'
          return
        }
        this.sendingAnnouncement = true
        const base = (process.env.VUE_APP_API_URL || 'http://127.0.0.1:5000').replace(/\/$/, '')
        const token = localStorage.getItem('authToken')
        const res = await fetch(`${base}/teacher_api/${this.currentUser.id}/${this.selectedClassCode}/announcements`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authentication-Token': token, 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ title: 'Announcement', content: this.announcementContent, priority: 'normal' })
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) {
          throw new Error(data?.error || 'Failed to send announcement')
        }
        this.announcementMessage = 'Announcement sent to class'
        this.announcementContent = ''
        try {
          const { default: toast } = await import('../services/toastService.js')
          toast.success('Announcement sent')
        } catch (e) { /* ignore */ }
      } catch (e) {
        this.announcementError = e?.message || 'Failed to send announcement'
      } finally {
        this.sendingAnnouncement = false
      }
    },
    async submitAnnouncementParents() {
      // Reuse the same endpoint; parents receive notification server-side
      await this.submitAnnouncementAll()
    },
    async submitCreateChallenge() {
      try {
        this.challengeCreateError = ''
        this.challengeCreateMessage = ''
        if (!this.currentUser?.id) {
          this.challengeCreateError = 'Missing teacher id'
          return
        }
        if (!this.newChallenge.title) {
          this.challengeCreateError = 'Title is required'
          return
        }
        this.creatingChallenge = true
        const { createChallenge, publishChallenge } = await import('../services/challengeService.js')
        const payload = { ...this.newChallenge, creator_id: this.currentUser.id }
        const res = await createChallenge(payload)
        if (!res?.success) {
          this.challengeCreateError = res?.error || 'Failed to create challenge'
          return
        }
        // Publish immediately
        await publishChallenge(res.data.id, { publish: true, creator_id: this.currentUser.id })
        this.challengeCreateMessage = 'Challenge created and published!'
        // Reset
        this.newChallenge = { title: '', description: '', category: '', difficulty: 'Beginner', duration: '7 days', start_date: '', end_date: '', target_count: 1, points: 100, icon: 'bi bi-trophy', goals: [] }
      } catch (e) {
        console.error('Create challenge failed:', e)
        this.challengeCreateError = e?.message || 'Failed to create challenge'
      } finally {
        this.creatingChallenge = false
      }
    },
    async submitCreateBadge() {
      try {
        this.badgeCreateError = ''
        this.badgeCreateMessage = ''
        if (!this.currentUser?.id) {
          this.badgeCreateError = 'Missing teacher id'
          return
        }
        if (!this.newBadge.title) {
          this.badgeCreateError = 'Title is required'
          return
        }
        this.creatingBadge = true
        const { createCustomBadge } = await import('../services/badgeService.js')
        const payload = {
          title: this.newBadge.title,
          icon: this.newBadge.icon,
          description: this.newBadge.description,
          category: this.newBadge.category || 'custom',
          difficulty: this.newBadge.difficulty || 'Beginner',
          required: this.newBadge.required || 1,
          creator_id: this.currentUser.id
        }
        // user_id is not used server-side for creation, but included per API signature
        await createCustomBadge(this.currentUser.id, this.currentUser.id, payload)
        this.badgeCreateMessage = 'Badge created! It will appear in students\' locked badges.'
        this.newBadge = { title: '', description: '', icon: '🏅', difficulty: 'Beginner', required: 1, category: '' }
      } catch (e) {
        console.error('Create badge failed:', e)
        this.badgeCreateError = e?.message || 'Failed to create badge'
      } finally {
        this.creatingBadge = false
      }
    },
    getWelcomeMessage() {
      if (!this.currentUser) return 'Welcome!'
      const hour = new Date().getHours()
      const greeting = hour < 12 ? 'Good Morning' : hour < 18 ? 'Good Afternoon' : 'Good Evening'
      return `${greeting}, ${this.currentUser.name}! Your classroom awaits your guidance.`
    },

    getSubtitleMessage() {
      return "Inspiring minds, one lesson at a time."
    },

    viewStudentProfile(studentName) {
      // Navigate to profile using slug as placeholder; in real data, use student.user_id
      const studentId = studentName.toLowerCase().replace(/\s+/g, '-')
      this.$router.push(`/student/${studentId}`)
    }
  },
  computed: {
    ringTransform() {
      const angle = Number.isFinite(this.classProgress.ringAngle) ? this.classProgress.ringAngle : 0
      return `rotate(-90deg) rotate(${angle}deg)`
    },
    donutStyle() {
      const pct = Math.max(0, Math.min(100, this.classProgress.completionRate || 0))
      const deg = (pct / 100) * 360
      // Create a conic-gradient based donut fill
      const color = getComputedStyle(document.documentElement).getPropertyValue('--role-primary') || '#0ea5e9'
      const track = getComputedStyle(document.documentElement).getPropertyValue('--bg-secondary') || '#e5e7eb'
      return {
        background: `conic-gradient(${color.trim()} ${deg}deg, ${track.trim()} ${deg}deg 360deg)`
      }
    }
  }
}
</script>

<style scoped>
.progress {
  background-color: var(--bg-secondary);
  border-radius: 4px;
}

.progress-bar {
  border-radius: 4px;
}

.form-select, .form-control {
  background: var(--bg-primary);
  color: var(--text-primary);
  border-color: var(--border-color);
}

.form-select:focus, .form-control:focus {
  background: var(--bg-primary);
  color: var(--text-primary);
  border-color: var(--role-primary);
  box-shadow: 0 0 0 0.2rem rgba(var(--role-primary-rgb), 0.25);
}

.form-select option {
  background: var(--bg-primary);
  color: var(--text-primary);
}

.table {
  color: var(--text-primary);
}

.table th {
  border-bottom: 2px solid var(--border-color);
  color: var(--text-primary);
}

.table td {
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);
}

.progress-ring {
  position: relative;
  display: inline-block;
}

.progress-donut {
  width: 100px;
  height: 100px;
  border-radius: 50%;
}

.progress-hole {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 72px;
  height: 72px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: var(--bg-primary);
  box-shadow: inset 0 0 0 6px var(--bg-primary), 0 0 0 6px var(--bg-primary);
}

.progress-ring-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 18px;
  font-weight: bold;
  color: var(--role-primary);
}



/* Ensure all text is visible in all themes */
.modern-card h5,
.modern-card h6,
.modern-card p,
.modern-card span,
.modern-card small,
.modern-card td,
.modern-card th {
  color: var(--text-primary) !important;
}

.modern-card .text-muted,
.modern-card small {
  color: var(--text-secondary) !important;
}

/* Fix table styling specifically */
.table {
  background: transparent !important;
}

.table th,
.table td {
  color: var(--text-primary) !important;
  border-color: var(--border-color) !important;
}

.table thead th {
  color: var(--text-primary) !important;
  background: transparent !important;
}

/* Fix placeholder text in dark mode */
.form-control::placeholder {
  color: var(--text-muted) !important;
}

/* Ensure badges remain visible */
.badge {
  color: white !important;
}

/* Force text colors to be visible against light gradient backgrounds in all sections */
.modern-card h1,
.modern-card h2,
.modern-card h3,
.modern-card h4,
.modern-card h5,
.modern-card h6,
.modern-card p,
.modern-card span,
.modern-card label,
.modern-card td,
.modern-card th,
.modern-card div,
.modern-card .form-control,
.modern-card .form-select {
  color: #1f2937 !important; /* Dark text for light backgrounds */
}

.modern-card .text-muted,
.modern-card small {
  color: #6b7280 !important; /* Medium gray for secondary text */
}

/* Ultra-strong overrides for ALL text elements in gradient cards */
.modern-card *:not(.badge):not(.btn):not(.progress-bar) {
  color: #1f2937 !important;
}

/* Table specific overrides for gradient sections */
.modern-card .table {
  color: #1f2937 !important;
}

.modern-card .table th,
.modern-card .table td {
  color: #1f2937 !important;
  border-color: rgba(31, 41, 55, 0.2) !important;
  background: transparent !important; /* Remove any dark row backgrounds */
}

.modern-card .table thead th {
  color: #1f2937 !important;
  background: transparent !important;
}

/* Override table row backgrounds specifically for gradient cards */
.modern-card .table tbody tr,
.modern-card .table tbody tr:nth-child(odd),
.modern-card .table tbody tr:nth-child(even) {
  background: transparent !important;
}

.modern-card .table tbody tr td,
.modern-card .table tbody tr:nth-child(odd) td,
.modern-card .table tbody tr:nth-child(even) td {
  background: transparent !important;
  color: #1f2937 !important;
}

/* Form controls in gradient sections */
.modern-card .form-control,
.modern-card .form-select {
  background: rgba(255, 255, 255, 0.8) !important;
  color: #1f2937 !important;
  border-color: rgba(31, 41, 55, 0.3) !important;
}

.modern-card .form-control::placeholder {
  color: #6b7280 !important;
}

.modern-card .form-control:focus,
.modern-card .form-select:focus {
  background: rgba(255, 255, 255, 0.9) !important;
  color: #1f2937 !important;
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.25) !important;
}

/* Performance table specific styles - ensure visibility in all themes */
.performance-table-card {
  background: var(--bg-primary) !important;
  border: 1px solid var(--border-color) !important;
}

.performance-table-card h1,
.performance-table-card h2,
.performance-table-card h3,
.performance-table-card h4,
.performance-table-card h5,
.performance-table-card h6,
.performance-table-card .table th,
.performance-table-card .table td,
.performance-table-card span,
.performance-table-card div,
.performance-table-card p {
  color: var(--text-primary) !important;
}

/* Ultra-strong override for performance table */
.performance-table-card *:not(.badge):not(.btn):not(.progress-bar) {
  color: var(--text-primary) !important;
}

.performance-table-card .table th {
  border-bottom: 2px solid var(--border-color) !important;
}

.performance-table-card .table td {
  border-bottom: 1px solid var(--border-color) !important;
}

/* Gradient override class for dynamic dark mode support */
.gradient-override {
  background: var(--bg-primary) !important;
  border: 1px solid var(--border-color) !important;
}

.gradient-override * {
  color: var(--text-primary) !important;
}

.gradient-override .text-muted,
.gradient-override small {
  color: var(--text-secondary) !important;
}

.gradient-override .badge {
  color: white !important;
}

/* Ultra-strong overrides for all text visibility */
.modern-card,
.modern-card *,
.table,
.table *,
.form-control,
.form-select {
  color: var(--text-primary) !important;
}

.modern-card .small,
.modern-card small,
.table .small,
.table small {
  color: var(--text-secondary) !important;
}

.modern-card .badge,
.table .badge {
  color: white !important;
}

/* Specific table overrides */
.table tbody tr,
.table tbody td,
.table thead tr,
.table thead th {
  color: var(--text-primary) !important;
  background: transparent !important;
  border-color: var(--border-color) !important;
}

/* Ensure form elements are visible */
.form-select,
.form-control {
  background: var(--bg-primary) !important;
  color: var(--text-primary) !important;
  border-color: var(--border-color) !important;
}

.form-select option {
  background: var(--bg-primary) !important;
  color: var(--text-primary) !important;
}

/* Progress elements */
.progress {
  background: var(--bg-secondary) !important;
}

.progress-bar {
  background: var(--role-primary) !important;
}

/* Force visibility for ANY text element */
h1, h2, h3, h4, h5, h6, p, span, div, td, th, small, strong, b, i, em {
  color: var(--text-primary) !important;
}

/* Override for muted text specifically */
.text-muted,
.small,
small {
  color: var(--text-secondary) !important;
}

/* Ensure emoji and icons are visible */
span[class*="emoji"],
span:contains("⭐"),
span:contains("📢"),
span:contains("📧") {
  opacity: 1 !important;
  visibility: visible !important;
}

/* Additional fixes for table content */
.table tr {
  background: transparent !important;
}

.table tr:nth-child(even) {
  background: var(--bg-secondary) !important;
}

.table tr:nth-child(odd) {
  background: transparent !important;
}

/* Ensure buttons are visible */
.btn {
  opacity: 1 !important;
  visibility: visible !important;
}



/* Light mode gradient backgrounds for specific cards */
.class-list-card:not(.gradient-override) {
  background: linear-gradient(135deg, #ecfeff 0%, #cffafe 100%) !important;
}

.assign-module-card:not(.gradient-override) {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%) !important;
}

.announcements-card:not(.gradient-override) {
  background: linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%) !important;
}

.class-progress-card:not(.gradient-override) {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%) !important;
}



/* Ensure text is visible in these specific cards */
.class-list-card *,
.assign-module-card *,
.announcements-card *,
.class-progress-card * {
  color: var(--text-primary) !important;
}

.class-list-card .small,
.class-list-card small,
.assign-module-card .small,
.assign-module-card small,
.announcements-card .small,
.announcements-card small,
.class-progress-card .small,
.class-progress-card small {
  color: var(--text-secondary) !important;
}

/* Modern Tabs Styling */
.modern-tabs .nav-link {
  border: none;
  border-bottom: 3px solid transparent;
  background: transparent;
  color: var(--text-secondary);
  font-weight: 500;
  padding: 1rem 1.5rem;
  transition: all 0.3s ease;
}

.modern-tabs .nav-link:hover {
  color: var(--text-primary);
  background: var(--bg-secondary);
}

.modern-tabs .nav-link.active {
  color: var(--role-primary);
  border-bottom-color: var(--role-primary);
  background: transparent;
}
</style> 